'use client'

import { useTranslations } from 'next-intl'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  getAvailableKinds,
  UnifiedInstrumentKind,
} from '@/features/order/order-form/lib/instrument-filter'
import { Instrument } from '@/types/generated/common/types'

interface InstrumentKindDropdownProps {
  base: string
  quote: string
  kind: UnifiedInstrumentKind
  onKindChange?: (newKind: UnifiedInstrumentKind) => void
  disabled?: boolean
  /** Caller's own exchange-scoped instrument list — see QuoteAssetDropdown. */
  instruments: Instrument[]
}

export function InstrumentKindDropdown({
  base,
  quote,
  kind,
  onKindChange,
  disabled,
  instruments,
}: InstrumentKindDropdownProps) {
  const t = useTranslations('orderForm')
  const availableKinds = getAvailableKinds(instruments, base, quote)
  const kindLabel = (value: UnifiedInstrumentKind) => t(`instrumentKind.${value}`)
  const kindShortLabel = (value: UnifiedInstrumentKind) => t(`instrumentKindShort.${value}`)

  if (availableKinds.length <= 1) {
    return (
      <span className="font-ibm-plex-sans text-foreground/50 shrink-0 text-sm whitespace-nowrap">
        {kindShortLabel(kind)}
      </span>
    )
  }

  return (
    <Select
      value={kind}
      onValueChange={(value) => onKindChange?.(value as UnifiedInstrumentKind)}
      disabled={disabled}
    >
      <SelectTrigger
        hideIcon
        data-1p-ignore
        tabIndex={-1}
        className="text-foreground/50 hover:text-foreground h-auto w-auto shrink-0 cursor-pointer border-none bg-transparent p-0 text-sm whitespace-nowrap transition-colors hover:underline disabled:opacity-50"
      >
        <SelectValue>{kindShortLabel(kind)}</SelectValue>
      </SelectTrigger>
      <SelectContent className="z-[60]">
        {availableKinds.map((k) => (
          <SelectItem key={k} value={k} className="hover:bg-foreground/10 cursor-pointer text-sm">
            {kindLabel(k)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
